import React, { useMemo } from 'react'
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import {
  H2,
  Button,
  Line,
  Flex,
  P,
  MultiInputFile,
  InputModal,
  Close,
  H4,
} from '../../lib/components'
import type {
  DividendPayoutLine,
  DividendPayoutPreviewResponse,
  DividendPayoutResponse,
} from '../../services/transaction-service'
import './dividend-payout-form.scss'

interface DividendPayoutFormPresenterProps {
  displayUploadModal: boolean
  onOpenUploadModal: () => void
  onCloseUploadModal: () => void
  dividendFiles: File[]
  onDividendFilesChange: (files: File[]) => void
  onUpload: () => void
  isUploading: boolean
  previewLoading: boolean
  previewError: string | null
  previews: DividendPayoutPreviewResponse[]
  confirmOpen: boolean
  onOpenConfirm: () => void
  onCloseConfirm: () => void
  onConfirmExecute: () => void
  isSubmitting: boolean
  results: DividendPayoutResponse[]
  resultError: string | null
}

const formatIsoDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

const formatDollars = (amount: number) =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const centsToDollars = (cents: number) => Math.round((cents / 100) * 100) / 100

const statusLabel = (s: DividendPayoutLine['status']): string => {
  switch (s) {
    case 'would_process':
    case 'processed':
      return 'Payout'
    case 'skipped_duplicate':
      return 'Skipped (duplicate)'
    case 'skipped_zero':
      return 'Skipped (zero)'
    default:
      return s
  }
}

const DIVIDEND_CSV_TEMPLATE =
  'opportunity_id,declaration_date,ex_dividend_date,dividend_payment_date,dividend_per_unit\n' +
  '1,2024-01-01,2024-01-15,2024-02-01,10.50\n'

const downloadTemplateCSV = () => {
  const blob = new Blob([DIVIDEND_CSV_TEMPLATE], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('hidden', '')
  a.setAttribute('href', url)
  a.setAttribute('download', 'dividend_declaration_template.csv')
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const DividendPayoutFormPresenter = ({
  displayUploadModal,
  onOpenUploadModal,
  onCloseUploadModal,
  dividendFiles,
  onDividendFilesChange,
  onUpload,
  isUploading,
  previewLoading,
  previewError,
  previews,
  confirmOpen,
  onOpenConfirm,
  onCloseConfirm,
  onConfirmExecute,
  isSubmitting,
  results,
  resultError,
}: DividendPayoutFormPresenterProps) => {
  const allLines = useMemo(
    () =>
      previews.flatMap((preview) =>
        (preview.payout_lines ?? []).map((line) => ({
          ...line,
          opportunity_title: preview.opportunity_title,
          opportunity_id: preview.opportunity_id,
          payment_date: preview.dividend_payment_date,
        })),
      ),
    [previews],
  )

  const actionableLines = useMemo(
    () => allLines.filter((line) => line.status === 'would_process'),
    [allLines],
  )

  const totalEstimatedDollars = useMemo(
    () => previews.reduce((sum, preview) => sum + (preview.total_estimated_payout_dollars || 0), 0),
    [previews],
  )

  const totalWouldCreate = useMemo(
    () => previews.reduce((sum, preview) => sum + (preview.would_create_transactions || 0), 0),
    [previews],
  )

  return (
    <Flex variant='column-center' classes={['dividend-payout-form-container']}>
      <H2 classes={['mb-48']} text='Dividend Payout' />

      <div className='instructions-container' data-testid='dividend-payout-instructions'>
        <P classes={['mb-16']} text='Process dividend payouts from a CSV of opportunity rates:' />
        <ul>
          <li className='instruction-item'>
            Prepare a CSV with columns: opportunity_id, declaration_date, ex_dividend_date,
            dividend_payment_date, dividend_per_unit
          </li>
          <li className='instruction-item'>
            dividend_per_unit is in dollars (e.g. 10.50). Dates use YYYY-MM-DD.
          </li>
          <li className='instruction-item'>
            Units are calculated from holdings at the ex-dividend date. Clients with a custom
            dividend_per_unit on opportunity terms use that rate instead.
          </li>
          <li className='instruction-item'>
            Upload creates declarations, then shows a preview. Confirm to create payouts and wallet
            credits.
          </li>
          <li className='instruction-item'>
            Duplicate payouts for the same investor, opportunity, and payment date are skipped.
          </li>
        </ul>
        <span className='csv-template-link' onClick={downloadTemplateCSV}>
          Download CSV Template
        </span>
      </div>

      <Line />

      <Flex variant='column-center' classes={['mt-24']}>
        <Button
          variant='primary-btn'
          text='Upload Dividend CSV'
          handleClick={onOpenUploadModal}
          dataTestId='dividend-payout-upload-btn'
        />
      </Flex>

      {previewLoading && (
        <Flex variant='column-center' classes={['mt-24']}>
          <CircularProgress size={32} />
          <P classes={['mt-16']} text='Building payout preview...' />
        </Flex>
      )}

      {previewError && (
        <P
          classes={['mt-16', 'error-text']}
          text={previewError}
          data-testid='dividend-preview-error'
        />
      )}

      {previews.length > 0 && !previewLoading && (
        <div className='preview-summary' data-testid='dividend-preview-summary'>
          <P
            classes={['mt-24']}
            text={`Preview ready: ${totalWouldCreate} payout(s) totaling $${formatDollars(
              totalEstimatedDollars,
            )}`}
          />
          <Button
            variant='primary-btn'
            text='Review & Confirm'
            handleClick={onOpenConfirm}
            dataTestId='dividend-payout-review-open'
            classes={['mt-16']}
          />
        </div>
      )}

      {results.length > 0 && (
        <div className='result-summary' data-testid='dividend-payout-result'>
          <P
            classes={['mt-24']}
            text={`Created ${results.reduce(
              (sum, r) => sum + r.total_transactions_created,
              0,
            )} payout transaction(s).`}
          />
        </div>
      )}

      {resultError && (
        <P
          classes={['mt-16', 'error-text']}
          text={resultError}
          data-testid='dividend-result-error'
        />
      )}

      {displayUploadModal && (
        <InputModal>
          <Flex
            variant='column'
            classes={['input-modal']}
            dataTestId='dividend-payout-upload-csv-modal'
          >
            <Flex variant='space-between'>
              <H4 text='Upload Dividend Declarations CSV' />
              <Close
                height='24px'
                onClick={() => {
                  if (!isUploading) {
                    onCloseUploadModal()
                  }
                }}
              />
            </Flex>
            <Flex variant='column' classes={['reply-section']}>
              <Line />
              <MultiInputFile
                name='dividend-csv'
                type='csv'
                handleFiles={(fileData) => {
                  if (isUploading) return
                  onDividendFilesChange(fileData ? (fileData as File[]) : [])
                }}
                label='Upload CSV file with dividend declarations'
                dataTestId='dividend-payout-upload-csv-file'
              />
              <Line />
              <Flex variant='row' classes={['mt-24', 'align-center']}>
                <Button
                  classes={isUploading ? ['mr-16'] : []}
                  variant='primary-btn'
                  text='Upload'
                  handleClick={onUpload}
                  isDisabled={isUploading || dividendFiles.length === 0}
                  dataTestId='dividend-payout-upload-submit'
                />
                {isUploading && <CircularProgress size={20} />}
              </Flex>
            </Flex>
          </Flex>
        </InputModal>
      )}

      <Dialog
        open={confirmOpen}
        onClose={onCloseConfirm}
        maxWidth='md'
        fullWidth
        classes={{ paper: 'dividend-payout-confirm-dialog' }}
      >
        <DialogTitle>Confirm dividend payouts</DialogTitle>
        <DialogContent>
          <P
            text={`About to create ${actionableLines.length} payout(s) totaling $${formatDollars(
              totalEstimatedDollars,
            )}.`}
            classes={['mb-16']}
          />
          <div className='confirm-table-wrap'>
            <table className='confirm-table' data-testid='dividend-confirm-table'>
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Investor</th>
                  <th>Units</th>
                  <th>Per unit</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th>Payment date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {allLines.map((line) => (
                  <tr key={`${line.opportunity_id}-${line.investor_id}-${line.status}`}>
                    <td>
                      {line.opportunity_title} (#{line.opportunity_id})
                    </td>
                    <td>#{line.investor_id}</td>
                    <td>{line.units_held}</td>
                    <td>${formatDollars(centsToDollars(line.dividend_per_unit_cents))}</td>
                    <td>{line.is_custom_rate ? 'Custom' : 'Opportunity'}</td>
                    <td>${formatDollars(centsToDollars(line.dividend_amount))}</td>
                    <td>{formatIsoDate(line.payment_date)}</td>
                    <td>{statusLabel(line.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant='secondary-btn'
            text='Cancel'
            handleClick={onCloseConfirm}
            dataTestId='dividend-payout-confirm-cancel'
          />
          <Button
            variant='primary-btn'
            text={isSubmitting ? 'Processing...' : 'Confirm payouts'}
            handleClick={onConfirmExecute}
            isDisabled={isSubmitting || actionableLines.length === 0}
            dataTestId='dividend-payout-confirm-submit'
          />
        </DialogActions>
      </Dialog>
    </Flex>
  )
}

export { DividendPayoutFormPresenter }
